/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/ //
import { IModuleStateManager } from '@msdyn365-commerce-modules/checkout-utilities';
import { Heading } from '@msdyn365-commerce-modules/data-types';
import { Node } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { AttributeDefinition, Customer, IContext } from '@msdyn365-commerce/retail-proxy';
import get from 'lodash/get';
import { observer } from 'mobx-react';
import * as React from 'react';
import { IBreadCrumbsRootData } from '../../../definition-extensions/checkout.ext.props.autogenerated';
import GuidedCard from './guided-card';

export interface ICheckoutGuidedFormProps {
    items: React.ReactNode[];
    moduleState: IModuleStateManager;
    disableGuidedCheckoutFlow?: boolean;
    isMobile?: boolean;
    isEditor?: boolean;
    rootLink?: IBreadCrumbsRootData;
    breadCrumbsText?: string[];
    placeOrderBtn?: React.ReactElement;
    attributes: AttributeDefinition[] | undefined;
    setIsDDRP: React.Dispatch<React.SetStateAction<boolean>>;
    isDDRP: boolean;
    customer: Customer;
    context: IContext;
    resource: {
        checkoutStepTitleFormat: string;
        saveBtnLabel: string;
        changeBtnLabel: string;
        cancelBtnLabel: string;
        saveAndContinueBtnLabel: string;
        reoccuringPaymentAttribute: string;
    };
}

interface ICheckoutGuidedFormState {
    currentStep: number;
    /* VSI Customization - START */
    activeView: number;
    currentCheckoutView: number;
    deliveryStepLink: React.ReactNode;
    checkoutModulesCount: number;
    isClickCollectOnly: boolean;
    /* VSI Customization - END */
}

/**
 *
 * CheckoutGuidedForm component
 * @extends {React.Component<ICheckoutGuidedFormProps>}
 */
@observer
class CheckoutGuidedForm extends React.Component<ICheckoutGuidedFormProps> {
    public state: ICheckoutGuidedFormState = {
        currentStep: 0,
        activeView: 0,
        currentCheckoutView: 1,
        deliveryStepLink: <></>,
        checkoutModulesCount: 0,
        isClickCollectOnly: false
    };
    public stepsToSkip: number = 0;

    public componentDidMount(): void {
        /**
         * Append child modules
         */
        const childIds = this.props.items.map((item: React.ReactNode) => get(item, 'props.id'));
        this.props.moduleState.init({ childIds });
        /* VSI Customization - START */
        const { items } = this.props;
        let checkoutItems = 0;
        let isClickCollect = false;
        items.map(item => {
            const childId = this.getId(item);
            const state = this.props.moduleState.getModule(childId);
            const { isDisabled, onSubmit } = state || ({} as IModuleStateManager);
            if (isDisabled) {
                this.stepsToSkip += 1;
                isClickCollect = true;
            }
            checkoutItems = onSubmit ? checkoutItems + 1 : checkoutItems;
        });
        this.setState({
            currentStep: this.stepsToSkip,
            activeView: this.stepsToSkip,
            currentCheckoutView: this.stepsToSkip < 2 ? 1 : 2,
            checkoutModulesCount: checkoutItems,
            isClickCollectOnly: isClickCollect
        });
        /* VSI Customization - END */
    }

    public render(): JSX.Element | null {
        const { moduleState, items, placeOrderBtn, resource, attributes, customer, isDDRP } = this.props;

        if (!items || !moduleState) {
            return null;
        }

        return (
            <>
                {/* VSI Customization - START */}
                {this._displayBreadCrumbs()}
                <Node tag='div' className='ms-checkout__guided-form'>
                    {items &&
                        items.map((item: React.ReactNode, index: number) => {
                            const childId = this.getId(item);
                            const step = this.getStep(childId);
                            const title = this.getHeading(item);
                            const state = this.props.moduleState.getModule(childId);
                            const {
                                isReady,
                                isPending,
                                isUpdating,
                                isDisabled,
                                isCancelAllowed,
                                onEdit,
                                onCancel,
                                onSubmit,
                                hasModuleState,
                                hasInitialized
                            } = state || ({} as IModuleStateManager);
                            /* VSI Customization - START */
                            // const _hidden = ((currentCheckoutView === 1 && index < 2) ||
                            //     (currentCheckoutView === 2 && index >= 2));
                            /* VSI Customization - END */
                            return (
                                <>
                                    <Node tag='div' className='' key={index}>
                                        <GuidedCard
                                            key={childId}
                                            step={step}
                                            title={title}
                                            resource={resource}
                                            disabled={!hasModuleState || isDisabled}
                                            isActive={this.isActive(step)}
                                            isVisted={this.isVisted(step)}
                                            isExpanded={this.isExpanded(step)}
                                            isSubmitting={isPending}
                                            isMobile={this.props.isMobile}
                                            isPending={isPending}
                                            isUpdating={isUpdating}
                                            hasInitialized={hasInitialized}
                                            isReady={isReady}
                                            onSubmit={onSubmit}
                                            onCancel={onCancel}
                                            isCancelAllowed={isCancelAllowed}
                                            onEdit={onEdit}
                                            onNext={this.onNext}
                                            hasControlGroup={!this.props.disableGuidedCheckoutFlow && hasInitialized}
                                            attributeDefinitions={attributes}
                                            setIsDDRP={this.props.setIsDDRP}
                                            isDDRP={isDDRP}
                                            customer={customer}
                                        >
                                            {React.cloneElement(item as React.ReactElement, { enableControl: true })}
                                        </GuidedCard>
                                    </Node>
                                </>
                            );
                        })}
                </Node>
                {/* VSI Customization - START - 27/11/20 */}

                {this.isLastStep() && placeOrderBtn}

                {/* VSI Customization - END */}
            </>
        );
    }
    /* VSI Customization - START */
    private _displayBreadCrumbs = (): React.ReactElement => {
        const { items, rootLink, breadCrumbsText } = this.props;
        const { activeView, checkoutModulesCount, isClickCollectOnly } = this.state;
        const rootLinkData: Msdyn365.ILinksData = {
            ariaLabel: rootLink && rootLink.ariaLabel,
            className: 'ms-breadcrumb_link-root',
            linkText: rootLink && rootLink.linkText,
            linkUrl: rootLink && rootLink.linkUrl.destinationUrl,
            openInNewTab: rootLink && rootLink.openInNewTab,
            role: 'link'
        };

        return (
            <Node tag='nav' className='ms-breadcrumb row' id='' role='navigation'>
                <Node tag='ol' className='ms-breadcrumb_list'>
                    <Node tag='li' className='ms-breadcrumbs_item'>
                        {rootLink && <Msdyn365.Link link={rootLinkData} />}
                    </Node>
                    {breadCrumbsText &&
                        items.map((item: React.ReactNode, index: number) => {
                            const childId = this.getId(item);
                            const state = this.props.moduleState.getModule(childId);
                            const { isDisabled } = state || ({} as IModuleStateManager);

                            const breadCrumbText = breadCrumbsText[index];
                            const breadCrumbLink: Msdyn365.ILinksData = {
                                className: 'ms-breadcrumb_link',
                                linkText: breadCrumbText,
                                role: 'link',
                                onClick: this.gotoSelectedModule(item, isClickCollectOnly)
                            };
                            // isDisbaled = true for hidden items in case of click and collect only
                            if (index <= activeView && isDisabled === false) {
                                return index === activeView || index === checkoutModulesCount - 1 ? (
                                    <Node tag='span' className='ms-breadcrumb_text'>
                                        {breadCrumbText}
                                    </Node>
                                ) : (
                                    <Msdyn365.Link link={breadCrumbLink} />
                                );
                            } else {
                                return null;
                            }
                        })}
                </Node>
            </Node>
        );
    };

    private gotoSelectedModule = (item: React.ReactNode, isClickCollectOnly: boolean) => {
        return () => {
            const childId = this.getId(item);
            let selectedModuleStep = this.getStep(childId);
            selectedModuleStep = isClickCollectOnly === true ? selectedModuleStep + 2 : selectedModuleStep;
            const state = this.props.moduleState.getModule(childId);
            const { onEdit } = state || ({} as IModuleStateManager);
            onEdit ? onEdit() : null;
            this.setState({
                currentStep: selectedModuleStep,
                activeView: selectedModuleStep,
                currentCheckoutView: selectedModuleStep < 2 ? 1 : 2
            });
        };
    };
    /* VSI Customization - END */
    private getEnabledModules = (): string[] => {
        const {
            moduleState: { childIds, getModule }
        } = this.props;
        return childIds.filter((childId: string) => {
            const state = getModule(childId);
            return !!state && !state.isDisabled;
        });
    };

    private getStep = (id: string): number => {
        return this.getEnabledModules().indexOf(id);
    };

    private getId = (item: React.ReactNode): string => {
        return get(item, 'props.id') || '';
    };

    private getHeading = (item: React.ReactNode): Heading => {
        return get(item, 'props.config.heading') || '';
    };

    private isExpanded = (step: number): boolean => {
        if (this.props.isEditor) {
            return true;
        }
        return step > -1 && step <= this.state.currentStep;
    };

    private isActive = (step: number): boolean => {
        return step + this.stepsToSkip === this.state.activeView;
    };

    private isVisted = (step: number): boolean => {
        return step > -1 && step < this.state.currentStep;
    };

    private isLastStep = (): boolean => {
        const { activeView, checkoutModulesCount } = this.state;
        const secondStep = checkoutModulesCount - 2;
        return activeView >= secondStep;
    };

    private onNext = (): void => {
        /* VSI Customization - START */
        this.setState({
            currentStep: this.state.currentStep + 1,
            activeView: this.state.activeView + 1,
            currentCheckoutView: this.state.currentStep < 1 ? 1 : 2
        });
        /* VSI Customization - END */
    };
}

export default CheckoutGuidedForm;
